import { useSyncExternalStore } from "react";

/**
 * Subscribes to a CSS media query via `useSyncExternalStore` — the hook
 * React ships specifically for reading a mutable external source (here,
 * `matchMedia`) without the cascading-render risk of setting state inside
 * an effect body. `getSnapshot` re-reads the query fresh on every render, so
 * this stays correct even if `query` itself changes between renders, not
 * just when the OS setting changes.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener("change", onChange);
      return () => mediaQueryList.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
  );
}

/**
 * Breakpoint thresholds from docs/ui/design-system.md §4 / docs/ui/layouts.md
 * — the single point where "desktop" vs. "mobile" is defined, so layout
 * components (`AppLayout` and, later, `MainAppLayout`/`AccountLayout`/etc.)
 * never hand-roll their own `min-width` string.
 */
export const BREAKPOINTS = {
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

/**
 * The one navigation-model threshold that matters for layout composition —
 * docs/ui/layouts.md is explicit that bottom-tabs vs. top-bar-and-sidebar
 * switches exactly once, at `lg` (1024px).
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}
